import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Send, Paperclip, File as FileIcon, Mic, Square, Smile, Reply, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import * as signalR from '@microsoft/signalr';
import {
  messageApi,
  getApiBaseUrl,
  getProfileImageUrl,
  getUploadedFileUrl,
  getMessageConversationAttachmentPlaybackUrl,
} from '../services/api';
import { MessageConversation, TaskConversationReaction, Employee } from '../types';
import { fixUtf8Mojibake } from '../utils/fixUtf8Mojibake';
import { ConversationVoiceAttachment } from './ConversationVoiceAttachment';

const REACTIONS = [
  { type: 'like', emoji: '👍' },
  { type: 'love', emoji: '❤️' },
  { type: 'laugh', emoji: '😂' },
  { type: 'wow', emoji: '😮' },
  { type: 'sad', emoji: '😢' },
  { type: 'angry', emoji: '😠' },
];

function sortConversations(list: MessageConversation[]): MessageConversation[] {
  return [...list].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

function mergeConversation(
  existing: MessageConversation[],
  incoming: MessageConversation
): MessageConversation[] {
  return sortConversations([
    ...existing.filter((c) => c.id !== incoming.id),
    incoming,
  ]);
}

interface MessageConversationPanelProps {
  messageId: number;
  conversations: MessageConversation[];
  employees: Employee[];
  onConversationsChange: (conversations: MessageConversation[], forMessageId?: number) => void;
  readOnly?: boolean;
  currentEmployeeId?: number;
  compact?: boolean;
  skipInitialFetch?: boolean;
  loading?: boolean;
}

const MessageConversationPanel: React.FC<MessageConversationPanelProps> = ({
  messageId,
  conversations,
  employees,
  onConversationsChange,
  readOnly = false,
  currentEmployeeId,
  compact = false,
  skipInitialFetch = false,
  loading = false,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [replyingTo, setReplyingTo] = useState<MessageConversation | null>(null);
  const [showReactionPicker, setShowReactionPicker] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const chatScrollContainerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const signalRConnectionRef = useRef<signalR.HubConnection | null>(null);
  const lastScrolledCountRef = useRef(0);
  const lastMessageIdRef = useRef<number | null>(null);
  const conversationsRef = useRef(conversations);
  const onConversationsChangeRef = useRef(onConversationsChange);
  conversationsRef.current = conversations;
  onConversationsChangeRef.current = onConversationsChange;

  const loadConversations = useCallback(async () => {
    if (!messageId) return;
    try {
      setLoadingConversations(true);
      const data = await messageApi.getConversations(messageId);
      onConversationsChangeRef.current(data, messageId);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [messageId]);

  useEffect(() => {
    lastScrolledCountRef.current = 0;
    lastMessageIdRef.current = null;
    setNewMessage('');
    setReplyingTo(null);
    setSelectedFiles([]);
    setShowReactionPicker(null);
    if (skipInitialFetch) return;
    loadConversations();
  }, [messageId, skipInitialFetch, loadConversations]);

  useEffect(() => {
    if (conversations.length === 0 || sendingMessage) return;
    const lastId = conversations[conversations.length - 1]?.id ?? null;
    const shouldScroll =
      conversations.length !== lastScrolledCountRef.current ||
      lastId !== lastMessageIdRef.current;
    if (!shouldScroll) return;

    lastScrolledCountRef.current = conversations.length;
    lastMessageIdRef.current = lastId;

    requestAnimationFrame(() => {
      if (chatScrollContainerRef.current) {
        chatScrollContainerRef.current.scrollTop = chatScrollContainerRef.current.scrollHeight;
      }
    });
  }, [conversations, sendingMessage]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadConversations();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadConversations]);

  useEffect(() => {
    const apiBaseUrl = getApiBaseUrl();
    const hubUrl = apiBaseUrl.endsWith('/api')
      ? apiBaseUrl.replace('/api', '/hubs/messageconversation')
      : `${apiBaseUrl.replace(/\/$/, '')}/hubs/messageconversation`;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .build();

    signalRConnectionRef.current = connection;

    connection.on('NewMessage', (conversation: MessageConversation) => {
      if (conversation.messageId !== messageId) return;
      onConversationsChangeRef.current(
        mergeConversation(conversationsRef.current, conversation),
        messageId
      );
    });

    connection.on('ReactionUpdated', (payload: { conversationId: number; reactions: TaskConversationReaction[] }) => {
      onConversationsChangeRef.current(
        conversationsRef.current.map((c) =>
          c.id === payload.conversationId ? { ...c, reactions: payload.reactions } : c
        ),
        messageId
      );
    });

    const joinGroup = async () => {
      await connection.invoke('JoinMessageGroup', messageId);
    };

    connection.onreconnected(() => {
      void joinGroup()
        .then(() => loadConversations())
        .catch((error) => {
          console.error('SignalR rejoin error:', error);
        });
    });

    const start = async () => {
      try {
        await connection.start();
        await joinGroup();
      } catch (error) {
        console.error('SignalR connection error:', error);
      }
    };
    start();

    return () => {
      connection.invoke('LeaveMessageGroup', messageId).catch(() => {});
      connection.stop().catch(() => {});
    };
  }, [messageId, loadConversations]);

  const formatRecordingTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-message-${Date.now()}.webm`, { type: 'audio/webm' });
        setSelectedFiles((prev) => [...prev, file]);
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (error) {
      alert('Could not access microphone.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly || sendingMessage || isRecording || loading) return;
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    try {
      setSendingMessage(true);
      const formData = new FormData();
      formData.append('message', newMessage.trim());
      if (replyingTo) formData.append('replyToConversationId', replyingTo.id.toString());
      selectedFiles.forEach((file) => formData.append('files', file));

      const sent = await messageApi.addConversation(messageId, formData);
      onConversationsChangeRef.current(
        mergeConversation(conversationsRef.current, sent),
        messageId
      );
      setNewMessage('');
      setReplyingTo(null);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleReaction = async (conversationId: number, reactionType: string) => {
    try {
      const result = await messageApi.addReaction(messageId, conversationId, reactionType);
      onConversationsChangeRef.current(
        conversationsRef.current.map((c) =>
          c.id === conversationId ? { ...c, reactions: result.reactions } : c
        ),
        messageId
      );
      setShowReactionPicker(null);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const getEmployeeProfile = (employeeId: number) => employees.find((e) => e.id === employeeId);

  return (
    <div className={compact ? 'flex flex-col h-full min-h-0 overflow-hidden' : 'border-t border-neutral-200 pt-4 mt-4'}>
      {!compact && <h3 className="text-sm font-semibold text-neutral-700 mb-3">Conversation</h3>}
      <div
        ref={chatScrollContainerRef}
        className={`flex-1 min-h-0 overflow-y-auto p-3 space-y-3 ${compact ? 'bg-neutral-50' : 'max-h-80 bg-neutral-50 rounded-lg border border-neutral-200 mb-3'}`}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
          </div>
        ) : loadingConversations && conversations.length === 0 ? (
          <div className="text-center text-neutral-500 py-6">Loading conversation...</div>
        ) : conversations.length === 0 ? (
          <div className="text-center text-neutral-500 py-6">No messages yet. Start the conversation below.</div>
        ) : (
          conversations.map((conv) => {
            const emp = conv.employee;
            const isOwn = currentEmployeeId != null && conv.employeeId === currentEmployeeId;
            const empProfileUrl = emp
              ? getProfileImageUrl(
                  emp.profileImageUrl ?? getEmployeeProfile(emp.id)?.profileImageUrl,
                  (emp as { updatedAt?: string }).updatedAt ?? getEmployeeProfile(emp.id)?.updatedAt
                )
              : null;
            return (
              <div key={conv.id} className={`flex gap-2 group ${isOwn ? 'flex-row-reverse' : ''}`}>
                {!isOwn && (
                  empProfileUrl ? (
                    <img src={empProfileUrl} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0 self-end" />
                  ) : (
                    <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 self-end">
                      {emp?.firstName?.[0] || 'U'}
                    </div>
                  )
                )}
                <div className={`flex-1 min-w-0 max-w-[75%] ${isOwn ? 'items-end flex flex-col' : ''}`}>
                  {!isOwn && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-neutral-700">
                        {emp?.firstName} {emp?.lastName}
                      </span>
                      <span className="text-xs text-neutral-400">
                        {format(new Date(conv.createdAt), 'HH:mm')}
                      </span>
                    </div>
                  )}
                  {conv.replyTo && (
                    <div className={`mb-1 pl-3 border-l-2 border-blue-300 bg-neutral-100/80 rounded p-2 text-xs max-w-full ${isOwn ? 'self-end' : ''}`}>
                      <div className="font-semibold text-neutral-700">
                        {conv.replyTo.employee.firstName} {conv.replyTo.employee.lastName}
                      </div>
                      <div className="text-neutral-600 line-clamp-2">{fixUtf8Mojibake(conv.replyTo.message)}</div>
                    </div>
                  )}
                  <div className={`rounded-2xl px-3 py-2 shadow-sm relative ${
                    isOwn
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white border border-neutral-200 text-neutral-800 rounded-bl-md'
                  }`}>
                    {isOwn && (
                      <span className="text-[10px] text-blue-100 block text-right mb-0.5">
                        {format(new Date(conv.createdAt), 'HH:mm')}
                      </span>
                    )}
                    {conv.message?.trim() && (
                      <p className={`text-sm whitespace-pre-wrap ${isOwn ? 'text-white' : 'text-neutral-700'}`}>{fixUtf8Mojibake(conv.message)}</p>
                    )}
                    {conv.attachments && conv.attachments.length > 0 && (
                      <div className={`space-y-2 ${conv.message?.trim() ? 'mt-2' : ''}`}>
                        {conv.attachments.map((attachment) => {
                          const isAudio =
                            attachment.contentType?.startsWith('audio/') ||
                            attachment.fileName?.match(/\.(webm|mp3|wav|m4a|aac)$/i) ||
                            attachment.fileName?.startsWith('voice-message-');
                          if (isAudio) {
                            return (
                              <div key={attachment.id} className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded">
                                <Mic className="h-4 w-4 text-purple-600 flex-shrink-0" />
                                <ConversationVoiceAttachment
                                  openHref={getUploadedFileUrl(attachment.fileUrl ?? attachment.filePath)}
                                  playbackUrl={getMessageConversationAttachmentPlaybackUrl(messageId, conv.id, attachment.id)}
                                  fileName={attachment.fileName}
                                />
                              </div>
                            );
                          }
                          return (
                            <a
                              key={attachment.id}
                              href={getUploadedFileUrl(attachment.fileUrl ?? attachment.filePath)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded hover:bg-neutral-100"
                            >
                              <FileIcon className="h-4 w-4 text-blue-600" />
                              <span className="text-sm truncate">{attachment.fileName}</span>
                              <span className="text-xs text-neutral-500">({formatFileSize(attachment.fileSize)})</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {!readOnly && (
                      <div className={`flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isOwn ? 'justify-end' : ''}`}>
                        <button
                          type="button"
                          onClick={() => setReplyingTo(conv)}
                          className={`text-xs flex items-center gap-1 ${isOwn ? 'text-blue-100 hover:text-white' : 'text-neutral-500 hover:text-blue-600'}`}
                        >
                          <Reply className="h-3 w-3" /> Reply
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowReactionPicker(showReactionPicker === conv.id ? null : conv.id)}
                            className={`text-xs flex items-center gap-1 ${isOwn ? 'text-blue-100 hover:text-white' : 'text-neutral-500 hover:text-blue-600'}`}
                          >
                            <Smile className="h-3 w-3" /> React
                          </button>
                          {showReactionPicker === conv.id && (
                            <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-white border border-neutral-200 rounded-lg shadow-lg p-1 z-10">
                              {REACTIONS.map((r) => (
                                <button
                                  key={r.type}
                                  type="button"
                                  onClick={() => handleReaction(conv.id, r.type)}
                                  className="hover:bg-neutral-100 rounded p-1 text-lg"
                                  title={r.type}
                                >
                                  {r.emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {conv.reactions && conv.reactions.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {conv.reactions.map((r) => (
                          <span
                            key={r.type}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-xs"
                          >
                            {REACTIONS.find((x) => x.type === r.type)?.emoji || r.type} {r.count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {!readOnly && !loading && (
        <div className={compact ? 'p-3 border-t border-neutral-200 bg-white flex-shrink-0' : ''}>
          {replyingTo && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded p-2 mb-2 text-sm">
              <span>
                Replying to {replyingTo.employee?.firstName} {replyingTo.employee?.lastName}
              </span>
              <button type="button" onClick={() => setReplyingTo(null)} className="text-neutral-500 hover:text-neutral-700">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {selectedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedFiles.map((file, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 rounded text-xs">
                  {file.name}
                  <button type="button" onClick={() => setSelectedFiles((f) => f.filter((_, i) => i !== idx))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <label
              htmlFor={`msg-file-${messageId}`}
              className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50 cursor-pointer flex items-center"
              title="Attach files"
            >
              <Paperclip className="h-5 w-5 text-neutral-600" />
            </label>
            <input id={`msg-file-${messageId}`} type="file" multiple onChange={handleFileSelect} className="hidden" />
            {isRecording ? (
              <button type="button" onClick={stopRecording} className="px-3 py-2 bg-red-600 text-white rounded-md flex items-center gap-2">
                <Square className="h-4 w-4" />
                {formatRecordingTime(recordingTime)}
              </button>
            ) : (
              <button type="button" onClick={startRecording} className="px-3 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50" title="Record voice">
                <Mic className="h-5 w-5 text-purple-600" />
              </button>
            )}
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={replyingTo ? 'Type your reply...' : 'Type your message...'}
              className="flex-1 px-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={sendingMessage || isRecording}
            />
            <button
              type="submit"
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || isRecording}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {sendingMessage ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default MessageConversationPanel;
